import { useEffect, useState } from 'react';

/*
 * "The mouse has stopped moving" — used to take the chrome off a fullscreen
 * call so the picture is not permanently framed by a control bar nobody is
 * reaching for.
 */

const IDLE_MS = 2500;

/**
 * True once the pointer has been still (and no key pressed) for `delay`.
 *
 * Listening on the window rather than the fullscreen element itself: while the
 * chrome is hidden the pointer is often over the video, and a `mousemove`
 * bound to the bar that is currently `pointer-events: none` would never wake
 * it again. Always false while `enabled` is false, so a windowed call keeps
 * its controls whatever the pointer does.
 */
export function usePointerIdle(enabled: boolean, delay = IDLE_MS): boolean {
  const [idle, setIdle] = useState(false);

  useEffect(() => {
    if (!enabled) {
      setIdle(false);
      return;
    }

    let timer = window.setTimeout(() => setIdle(true), delay);
    const wake = () => {
      setIdle(false);
      window.clearTimeout(timer);
      timer = window.setTimeout(() => setIdle(true), delay);
    };

    // pointermove covers mouse and pen; a touch screen has no hover, so a tap
    // (pointerdown) is the only "the user is here" signal it can give.
    window.addEventListener('pointermove', wake);
    window.addEventListener('pointerdown', wake);
    window.addEventListener('keydown', wake);
    window.addEventListener('wheel', wake, { passive: true });
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener('pointermove', wake);
      window.removeEventListener('pointerdown', wake);
      window.removeEventListener('keydown', wake);
      window.removeEventListener('wheel', wake);
    };
  }, [enabled, delay]);

  return enabled && idle;
}
